import React from "react";
import { WhatsApp, EmailOutlined, AccessTimeRounded } from "@mui/icons-material";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Button,
  Tooltip,
} from "@mui/material";
import { Stack } from "@mui/system";
import { useAppDispatch, useAppSelector } from "src/app/hooks";
import { RevisionGenericWrapper } from "./revision-generic-wrapper";
import { approvalCreateSlice } from "src/features/approvalsCreate/approvalCreateSlice";
import { IApproval } from "src/services/thumbz-base-api";
import { useBillingStatus } from "src/hooks/use-billing";

interface IRevisionNotificationsProps {
  approval: IApproval;
}

export const RevisionNotifications: React.FC<IRevisionNotificationsProps> = ({ approval }) => {
  const isLoading = useAppSelector(approvalCreateSlice.selectors.isStep4Loading);
  const notificationConfig = approval.notificationConfig;
  const { data: billing } = useBillingStatus();
  const isFreePlan = String(billing.plan?.slug).toLowerCase() === "free";

  const dispatch = useAppDispatch();
  function _goToStep3() {
    dispatch(approvalCreateSlice.actions.changeStep(2));
  }

  return (
    <Stack flex={1} flexGrow={1}>
      <RevisionGenericWrapper
        title={"Notificações"}
        description="Reveja como os aprovadores serão notificados"
        action={
          <Button variant="text" color="primary" onClick={_goToStep3} disabled={isLoading}>
            Editar
          </Button>
        }
      >
        <List dense>
          {isFreePlan ? (
            <ListItem disableGutters>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <WhatsApp fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography variant="body2" fontWeight={500}>
                      WhatsApp
                    </Typography>
                    <Tooltip title="Disponível apenas em planos pagos">
                      <WorkspacePremiumIcon
                        fontSize="inherit"
                        sx={(theme) => ({
                          color:
                            theme.palette.mode === "light"
                              ? theme.palette.warning.dark
                              : theme.palette.warning.light,
                        })}
                      />
                    </Tooltip>
                  </Box>
                }
                secondary={
                  <Typography variant="body2" color="text.secondary">
                    Disponível apenas no plano pago.
                  </Typography>
                }
              />
            </ListItem>
          ) : (
            <ListItem disableGutters>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <WhatsApp fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body2" fontWeight={500}>
                    WhatsApp
                  </Typography>
                }
                secondary={
                  notificationConfig ? (
                    <Typography variant="body2" color="text.secondary">
                      {notificationConfig.anc_whatsapp_enabled
                        ? "Ativado — os aprovadores receberão notificações por WhatsApp."
                        : "Desativado — notificações por WhatsApp não serão enviadas."}
                    </Typography>
                  ) : (
                    "Configuração não disponível."
                  )
                }
              />
            </ListItem>
          )}
          <ListItem disableGutters>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <EmailOutlined fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="body2" fontWeight={500}>
                  E-mail
                </Typography>
              }
              secondary={
                notificationConfig ? (
                  <Typography variant="body2" color="text.secondary">
                    {notificationConfig.anc_email_enabled
                      ? "Ativado — os aprovadores receberão notificações por e-mail."
                      : "Desativado — notificações por e-mail não serão enviadas."}
                  </Typography>
                ) : (
                  "Configuração não disponível."
                )
              }
            />
          </ListItem>
        </List>

        {notificationConfig && (
          <List dense sx={{ mt: 0, pt: 0 }}>
            <ListItem disableGutters>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <AccessTimeRounded fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body2" fontWeight={500}>
                    Configurações de envio
                  </Typography>
                }
                secondary={
                  <>
                    <Typography variant="body2" color="text.secondary">
                      Horário preferencial: {notificationConfig.anc_preferred_time}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Intervalo entre lembretes: {notificationConfig.anc_interval_days} dia(s)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Máximo de lembretes: {notificationConfig.anc_max_reminders}
                    </Typography>
                  </>
                }
              />
            </ListItem>
          </List>
        )}
      </RevisionGenericWrapper>
    </Stack>
  );
};
